import { MODEL_TOTAL } from '@/constants/modelInventory.generated';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { Link } from 'expo-router';

import ClosingBand from '@/components/Landing/ClosingBand';
import FlowField from '@/components/Landing/FlowField';
import {
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  PrimaryCta,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  Band,
  Card,
  CheckLine,
  IconTile,
  MicroLabel,
  Ordinal,
  RuledGrid,
  SectionIntro,
} from '@/components/Landing/MarketingKit';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import UseCaseExamplePrompts from '@/components/Landing/UseCaseExamplePrompts';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import { ACCENT, EASE, LAYOUT, MONO, SERIF, isWeb } from '@/components/Landing/landingTokens';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { isDarkTheme } from '@/constants/Colors';
import { FREE_USER_STARTING_CREDITS_DISPLAY, GAB_AI_SOFTWARE_ID } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';

const SITE_URL = 'https://gab.ai';

function buildVerticalStructuredData(data) {
  const pageUrl = `${SITE_URL}/use-cases/${data.slug}`;
  const graph = [
    {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      name: data.seo.title,
      description: data.seo.description,
      url: pageUrl,
      isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
      inLanguage: 'en-US',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Use Cases', item: `${SITE_URL}/use-cases` },
        { '@type': 'ListItem', position: 3, name: data.name, item: pageUrl },
      ],
    },
    {
      '@type': 'SoftwareApplication',
      '@id': GAB_AI_SOFTWARE_ID,
      name: 'Gab AI',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web, iOS, Android, macOS',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
  ];

  if (Array.isArray(data.tools) && data.tools.length) {
    graph.push({
      '@type': 'ItemList',
      name: `Gab AI tools for ${data.name.toLowerCase()}`,
      itemListElement: data.tools.map((tool, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: tool.name,
        description: tool.desc,
      })),
    });
  }

  if (Array.isArray(data.faqs) && data.faqs.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: data.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

const COMPARISON_ROWS = [
  { feature: 'Every top AI model in one place', gab: true, others: false },
  { feature: 'Generate images, video, and music', gab: true, others: false },
  { feature: 'Anonymized conversations', gab: true, others: false },
  { feature: 'Never trains on your data', gab: true, others: false },
  { feature: 'Natural real-time voice conversations', gab: true, others: false },
  { feature: 'Automated scheduled tasks', gab: true, others: false },
  { feature: 'Multi-model side-by-side responses', gab: true, others: false },
  { feature: 'AI that remembers your preferences', gab: true, others: false },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Create Your Free Account',
    desc: `Sign up in seconds. No credit card required. You get ${FREE_USER_STARTING_CREDITS_DISPLAY} free credits to explore everything.`,
  },
  {
    step: '2',
    title: 'Start Creating',
    desc: 'Type your request to create, or use Voice Mode when you want to talk through an idea hands-free. Our AI routes each task to the best model.',
  },
  {
    step: '3',
    title: 'Get Results Instantly',
    desc: 'Your AI learns your preferences over time. The more you use it, the better it gets at delivering exactly what you need.',
  },
];

const WHY_LEAD = {
  icon: 'cubes',
  title: `${MODEL_TOTAL} AI Models in One Chat`,
  desc: 'GPT-5, Claude, Gemini, DeepSeek, Qwen, and more. Switch between models with one click, or let our smart routing pick the best one for each task. When new models drop, they\'re added automatically.',
};

const WHY_SIDE = {
  icon: 'clone',
  title: 'Multi-Model Responses',
  desc: 'Send one prompt, get answers from GPT, Claude, and Gemini side by side. Compare and pick the best.',
};

const WHY_ROW = [
  { icon: 'brain', title: 'Personalized Memory', desc: 'AI that learns your preferences, style, and context. Gets smarter the more you use it.' },
  { icon: 'microphone', title: 'Voice Mode', desc: 'Talk naturally with AI for brainstorming, follow-ups, search, practice, and hands-free help.' },
  { icon: 'clock-rotate-left', title: 'Automated Tasks', desc: 'Schedule AI to deliver daily briefings, research, and content while you sleep.' },
];

const PRIVACY_FEATURES = [
  { icon: 'user-secret', title: 'Anonymous by Design', desc: 'Every request to third-party AI models is completely anonymized. No one can trace responses back to you. Ever.' },
  { icon: 'ban', title: 'We Never Train on Your Data', desc: 'Your conversations are never used to train AI models. Period. What you say stays between you and your AI.' },
  { icon: 'eye-slash', title: 'No Surveillance, No Ads', desc: 'We don\'t profile you, track your behavior, or sell your data to advertisers. Your AI is yours alone.' },
];

function headlineLines(value, fallback) {
  const lines = String(value || '').split('\n').map((line) => line.trim()).filter(Boolean);
  return lines.length ? lines : [fallback];
}

function PageSection({ label, children, style, contentStyle }) {
  const { pad, space } = useLanding();
  return (
    <SemanticBlock
      tag="section"
      aria-label={label}
      style={{
        width: '100%',
        alignSelf: 'stretch',
        alignItems: 'center',
        ...style,
      }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: LAYOUT.max,
          paddingHorizontal: pad,
          paddingTop: space,
          paddingBottom: space,
          ...contentStyle,
        }}
      >
        {children}
      </View>
    </SemanticBlock>
  );
}

function PremiumNote() {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Link href="/pricing" asChild>
      <Pressable
        {...handlers}
        accessibilityRole="link"
        style={{
          marginTop: 24,
          maxWidth: 840,
          paddingVertical: 16,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          borderTopWidth: 1,
          borderBottomWidth: 1,
          borderColor: hovered ? 'rgba(13,92,71,0.28)' : c.rule,
          ...(isWeb ? { cursor: 'pointer', transition: `border-color 180ms ${EASE}` } : {}),
        }}
      >
        <Text tag="p" style={{ fontSize: 14, lineHeight: 21, color: c.muted, marginBottom: 0, flex: 1 }}>
          <Text tag="span" weight="semibold" style={{ color: ACCENT }}>Plus Premium</Text>
          {': 12,000 credits/month, 50,000 API requests/day, and priority support. $99/mo or $999/yr.'}
        </Text>
        <Icon name="arrow-right" size={13} color={ACCENT} accessibilityElementsHidden />
      </Pressable>
    </Link>
  );
}

function RelatedRow({ item }) {
  const { c, isXS } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Link href={`/use-cases/${item.slug}`} asChild>
      <Pressable
        {...handlers}
        accessibilityRole="link"
        style={{
          width: isXS ? '100%' : 'calc(50% - 8px)',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 14,
          paddingVertical: 18,
          borderTopWidth: 1,
          borderTopColor: c.rule,
          ...(isWeb ? { cursor: 'pointer' } : {}),
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text
            tag="h3"
            weight="semibold"
            style={{
              color: hovered ? ACCENT : c.ink,
              fontSize: 16.5,
              lineHeight: 22,
              ...(isWeb ? { transition: `color 180ms ${EASE}` } : {}),
            }}
          >
            {item.name}
          </Text>
          {item.eyebrow ? (
            <Text tag="p" numberOfLines={2} style={{ color: c.muted, fontSize: 13.5, lineHeight: 20, marginBottom: 0 }}>
              {item.eyebrow}
            </Text>
          ) : null}
        </View>
        <Icon name="arrow-right" size={13} color={hovered ? ACCENT : c.faint} accessibilityElementsHidden />
      </Pressable>
    </Link>
  );
}

export default function VerticalTemplate({ data, relatedUseCases = [] }) {
  const { colors, theme } = useTheme();
  const { c, band, isXS, isCompact } = useLanding();
  const isDark = isDarkTheme(theme);
  const tools = Array.isArray(data.tools) ? data.tools : [];
  const painPoints = Array.isArray(data.painPoints) ? data.painPoints : [];
  const capabilities = Array.isArray(data.capabilities) ? data.capabilities : [];
  const faqs = Array.isArray(data.faqs) ? data.faqs : [];

  useLandingStyles();

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title={data.seo.title}
        description={data.seo.description}
        canonical={`/use-cases/${data.slug}`}
        keywords={data.seo.keywords}
        image={data.seo.ogImage || undefined}
        structuredData={buildVerticalStructuredData(data)}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <SemanticBlock
          tag="header"
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
          }}
        >
          <View
            style={{
              width: '100%',
              maxWidth: LAYOUT.max,
              paddingHorizontal: isXS ? 20 : 40,
              paddingTop: isXS ? 108 : 148,
              paddingBottom: isXS ? 48 : 64,
            }}
          >
            <View style={{ maxWidth: 840 }}>
              {data.eyebrow ? <Kicker>{data.eyebrow}</Kicker> : null}
              <DisplayHeading
                tag="h1"
                mask={false}
                type={{
                  fontSize: isXS ? 36 : isCompact ? 52 : 64,
                  lineHeight: isXS ? 42 : isCompact ? 58 : 70,
                  letterSpacing: isXS ? -1 : -2,
                }}
                style={{ marginTop: data.eyebrow ? 22 : 0 }}
                lines={headlineLines(data.headline, data.name)}
              />
              {data.subtitle ? (
                <Lede max={640} style={{ marginTop: 20 }}>
                  {data.subtitle}
                </Lede>
              ) : null}
              <CtaRow style={{ marginTop: 32 }}>
                <PrimaryCta label={data.cta} href="/signup" full={isXS} />
                <GhostCta label="View pricing" href="/pricing" />
              </CtaRow>
              {data.ctaSubtext ? (
                <Text tag="p" style={{ color: c.faint, fontSize: 14, marginTop: 16 }}>
                  {data.ctaSubtext}
                </Text>
              ) : null}
            </View>
          </View>
        </SemanticBlock>

        <UseCaseExamplePrompts
          useCaseName={data.name}
          examplePrompts={data.examplePrompts}
          promptCategories={data.promptCategories}
          cta={data.cta}
        />

        <PageSection label="Gab AI vs. The Rest">
          <SectionIntro
            kicker="The comparison"
            lines={['Gab AI vs. the rest']}
            lede="See why thousands are switching to the all-in-one AI platform."
            ledeMax={480}
          />
          <View
            accessibilityRole={safeAccessibilityRole('table')}
            style={{
              width: '100%',
              maxWidth: 720,
              marginTop: 36,
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderColor: c.rule,
            }}
          >
            <View
              accessibilityRole={safeAccessibilityRole('row')}
              style={{
                flexDirection: 'row',
                paddingVertical: isXS ? 14 : 16,
                borderBottomWidth: 1,
                borderBottomColor: c.rule,
              }}
            >
              <View style={{ flex: 1 }} accessibilityRole={safeAccessibilityRole('columnheader')}>
                <MicroLabel>Feature</MicroLabel>
              </View>
              <View style={{ width: isXS ? 72 : 96, alignItems: 'center' }} accessibilityRole={safeAccessibilityRole('columnheader')}>
                <Text weight="bold" style={{ color: ACCENT, fontSize: 12, letterSpacing: 0.3 }}>Gab AI</Text>
              </View>
              <View style={{ width: isXS ? 72 : 96, alignItems: 'center' }} accessibilityRole={safeAccessibilityRole('columnheader')}>
                <Text weight="medium" style={{ color: c.faint, fontSize: 12, letterSpacing: 0.3 }}>Others</Text>
              </View>
            </View>
            {COMPARISON_ROWS.map((row) => (
              <View
                key={row.feature}
                accessibilityRole={safeAccessibilityRole('row')}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: isXS ? 12 : 15,
                  borderBottomWidth: 1,
                  borderBottomColor: c.rule,
                }}
              >
                <View style={{ flex: 1 }} accessibilityRole={safeAccessibilityRole('cell')}>
                  <Text tag="span" style={{ color: c.ink, fontSize: isXS ? 14 : 15, lineHeight: 22 }}>
                    {row.feature}
                  </Text>
                </View>
                <View style={{ width: isXS ? 72 : 96, alignItems: 'center' }} accessibilityRole={safeAccessibilityRole('cell')}>
                  <Icon name="check" size={13} color={ACCENT} accessibilityElementsHidden />
                </View>
                <View style={{ width: isXS ? 72 : 96, alignItems: 'center' }} accessibilityRole={safeAccessibilityRole('cell')}>
                  <Icon name="xmark" size={12} color={c.faint} accessibilityElementsHidden />
                </View>
              </View>
            ))}
          </View>
          <CtaRow style={{ marginTop: 36 }}>
            <PrimaryCta label={data.cta} href="/signup" full={isXS} />
          </CtaRow>
        </PageSection>

        {painPoints.length > 0 ? (
          <PageSection label="The problems we solve">
            <SectionIntro
              kicker="The problem"
              lines={['Sound familiar?']}
              lede="These are the problems we solve, so you can focus on what you do best."
            />
            <RuledGrid
              items={painPoints}
              keyOf={(_, index) => `pain-${index}`}
              edges="both"
              style={{ marginTop: 36 }}
              renderItem={(item, index) => (
                <SemanticBlock tag="article" style={{ gap: 14 }}>
                  <Ordinal index={index} />
                  <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                    {item.problem}
                  </Text>
                  <Text
                    tag="p"
                    weight="semibold"
                    style={{
                      color: c.ink,
                      fontFamily: SERIF,
                      fontStyle: 'italic',
                      fontSize: 17,
                      lineHeight: 26,
                    }}
                  >
                    {item.solution}
                  </Text>
                </SemanticBlock>
              )}
            />
          </PageSection>
        ) : null}

        {tools.length > 0 ? (
          <PageSection label={`Purpose-built AI tools for ${data.name.toLowerCase()}`}>
            <SectionIntro
              kicker="Your toolkit"
              lines={['Everything you need']}
              lede={`Purpose-built AI tools for ${data.name.toLowerCase()}, all included in one platform.`}
            />
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 0,
                marginTop: 36,
                borderTopWidth: 1,
                borderColor: c.rule,
              }}
            >
              {tools.map((tool, index) => (
                <SemanticBlock
                  key={tool.name}
                  tag="article"
                  style={{
                    width: isXS ? '100%' : '50%',
                    paddingTop: 26,
                    paddingBottom: 26,
                    paddingRight: isXS || index % 2 === 1 ? 0 : 28,
                    paddingLeft: isXS || index % 2 === 0 ? 0 : 28,
                    borderBottomWidth: 1,
                    borderRightWidth: isXS || index % 2 === 1 ? 0 : 1,
                    borderColor: c.rule,
                    gap: 14,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <IconTile name={tool.icon} size={36} tone="default" />
                    <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 17, flex: 1 }}>
                      {tool.name}
                    </Text>
                  </View>
                  <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                    {tool.desc}
                  </Text>
                </SemanticBlock>
              ))}
            </View>
            <CtaRow style={{ marginTop: 36 }}>
              <PrimaryCta label={data.cta} href="/signup" full={isXS} />
            </CtaRow>
          </PageSection>
        ) : null}

        <PageSection label="How it works">
          <SectionIntro
            kicker="How it works"
            lines={['Start in seconds, not hours.']}
            lede="No downloads, no complex setup. Sign up and start creating immediately."
          />
          <RuledGrid
            items={HOW_IT_WORKS}
            keyOf={(item) => item.step}
            edges="both"
            style={{ marginTop: 36 }}
            renderItem={(item) => (
              <SemanticBlock tag="article" style={{ gap: 12 }}>
                <Text
                  weight="bold"
                  style={{
                    color: ACCENT,
                    fontFamily: MONO,
                    fontSize: isXS ? 28 : 36,
                    lineHeight: isXS ? 32 : 40,
                    fontVariant: ['tabular-nums'],
                  }}
                >
                  {item.step}
                </Text>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18 }}>
                  {item.title}
                </Text>
                <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                  {item.desc}
                </Text>
              </SemanticBlock>
            )}
          />
          <CtaRow style={{ marginTop: 36 }}>
            <PrimaryCta label="Create Your Free Account" href="/signup" full={isXS} />
          </CtaRow>
        </PageSection>

        {capabilities.length > 0 ? (
          <PageSection label="What you can do">
            <SectionIntro kicker="Capabilities" lines={['What you can do']} />
            <View style={{ maxWidth: 720, marginTop: 32, gap: 16 }}>
              {capabilities.map((capability) => (
                <CheckLine key={capability} size={16}>{capability}</CheckLine>
              ))}
            </View>
          </PageSection>
        ) : null}

        <PageSection label="Why Gab AI">
          <SectionIntro
            kicker="Why Gab AI"
            lines={['One platform. Everything you need.']}
            lede="Stop paying for multiple AI subscriptions. Gab AI combines every capability into one powerful platform."
          />
          <View style={{ gap: 16, marginTop: 36 }}>
            <View style={{ flexDirection: isXS ? 'column' : 'row', gap: 16 }}>
              <SemanticBlock tag="article" style={{ flex: isXS ? undefined : 2 }}>
                <Card style={{ height: '100%', gap: 16 }}>
                  <IconTile name={WHY_LEAD.icon} />
                  <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 22, letterSpacing: -0.3 }}>
                    {WHY_LEAD.title}
                  </Text>
                  <Text tag="p" style={{ color: c.muted, fontSize: 16, lineHeight: 25 }}>
                    {WHY_LEAD.desc}
                  </Text>
                </Card>
              </SemanticBlock>
              <SemanticBlock tag="article" style={{ flex: isXS ? undefined : 1 }}>
                <Card style={{ height: '100%', gap: 16 }}>
                  <IconTile name={WHY_SIDE.icon} tone="default" />
                  <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18 }}>
                    {WHY_SIDE.title}
                  </Text>
                  <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                    {WHY_SIDE.desc}
                  </Text>
                </Card>
              </SemanticBlock>
            </View>
            <RuledGrid
              items={WHY_ROW}
              keyOf={(item) => item.title}
              edges="both"
              renderItem={(item) => (
                <SemanticBlock tag="article" style={{ gap: 12 }}>
                  <IconTile name={item.icon} size={36} tone="default" />
                  <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 17 }}>
                    {item.title}
                  </Text>
                  <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                    {item.desc}
                  </Text>
                </SemanticBlock>
              )}
            />
            <Card
              style={{
                flexDirection: isXS ? 'column' : 'row',
                alignItems: isXS ? 'flex-start' : 'center',
                gap: isXS ? 18 : 28,
              }}
            >
              <IconTile name="folder-open" />
              <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18 }}>
                  One Dashboard, One Subscription
                </Text>
                <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
                  Stop juggling multiple AI apps and subscriptions. Text, images, video, music, voice, code, all organized in one beautiful interface.
                </Text>
              </View>
              <PrimaryCta label="Get Started" href="/signup" full={isXS} />
            </Card>
          </View>
        </PageSection>

        <Band glow="18% 28%">
          <PageSection label="Your privacy matters" style={{ backgroundColor: 'transparent' }}>
            <SectionIntro
              tone="band"
              kicker="Privacy first"
              lines={['Your privacy matters']}
              lede="Unlike Big Tech AI platforms that harvest your data, Gab AI was built from the ground up with privacy at its core."
            />
            <RuledGrid
              items={PRIVACY_FEATURES}
              keyOf={(item) => item.title}
              tone="band"
              edges="both"
              style={{ marginTop: 36 }}
              renderItem={(item) => (
                <SemanticBlock tag="article" style={{ gap: 12 }}>
                  <IconTile name={item.icon} tone="band" />
                  <Text tag="h3" weight="semibold" style={{ color: band.ink, fontSize: 18 }}>
                    {item.title}
                  </Text>
                  <Text tag="p" style={{ color: band.muted, fontSize: 15, lineHeight: 23 }}>
                    {item.desc}
                  </Text>
                </SemanticBlock>
              )}
            />
            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 12 : 28,
                marginTop: 32,
              }}
            >
              {['No data selling', 'No targeted ads', 'Delete anytime', 'You own your data'].map((item) => (
                <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Icon name="check" size={11} color={ACCENT} accessibilityElementsHidden />
                  <Text tag="span" weight="medium" style={{ color: band.muted, fontSize: 14 }}>{item}</Text>
                </View>
              ))}
            </View>
          </PageSection>
        </Band>

        {faqs.length > 0 ? (
          <PageSection label="Common questions">
            <SectionIntro kicker="FAQ" lines={['Common questions']} />
            <View style={{ maxWidth: 760, marginTop: 28 }}>
              {faqs.map((faq, index) => (
                <SemanticBlock
                  key={faq.q}
                  tag="article"
                  style={{
                    paddingVertical: 24,
                    borderTopWidth: 1,
                    borderTopColor: c.rule,
                    gap: 10,
                  }}
                >
                  <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: isXS ? 17 : 18, lineHeight: 26 }}>
                    {faq.q}
                  </Text>
                  <Text tag="p" style={{ color: c.muted, fontSize: 15.5, lineHeight: 25 }}>
                    {faq.a}
                  </Text>
                  {index === faqs.length - 1 ? (
                    <View style={{ height: 1, backgroundColor: c.rule, marginTop: 14 }} />
                  ) : null}
                </SemanticBlock>
              ))}
            </View>
          </PageSection>
        ) : null}

        <PageSection label="Simple pricing">
          <SectionIntro
            kicker="Simple pricing"
            lines={['Start free. Upgrade when ready.']}
            lede="No hidden fees. No per-model charges. One subscription, everything included."
          />
          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              gap: 16,
              maxWidth: 840,
              marginTop: 36,
            }}
          >
            <SemanticBlock tag="article" style={{ flex: 1 }}>
              <Card style={{ gap: 8, height: '100%' }}>
                <MicroLabel>Free</MicroLabel>
                <Text weight="bold" style={{ color: c.ink, fontSize: 40, letterSpacing: -1.2, marginTop: 8 }}>$0</Text>
                <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23, marginBottom: 16 }}>
                  {FREE_USER_STARTING_CREDITS_DISPLAY} credits to explore everything. No credit card required.
                </Text>
                <View style={{ gap: 12 }}>
                  {['Access every AI model', 'Generate images, video, and music', 'Voice conversations', 'Personalized memory'].map((item) => (
                    <CheckLine key={item}>{item}</CheckLine>
                  ))}
                </View>
              </Card>
            </SemanticBlock>
            <SemanticBlock tag="article" style={{ flex: 1 }}>
              <Card
                style={{
                  gap: 8,
                  height: '100%',
                  borderColor: 'rgba(13,92,71,0.28)',
                  backgroundColor: c.accentWash,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <MicroLabel>Plus</MicroLabel>
                  <Text weight="bold" style={{ color: ACCENT, fontSize: 10.5, letterSpacing: 0.16, textTransform: 'uppercase' }}>
                    Popular
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 8 }}>
                  <Text weight="bold" style={{ color: c.ink, fontSize: 40, letterSpacing: -1.2 }}>$16.67</Text>
                  <Text style={{ color: c.muted, fontSize: 15, marginLeft: 6 }}>/mo</Text>
                </View>
                <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23, marginBottom: 16 }}>
                  Billed annually at $200/year. Unlimited default model messages.
                </Text>
                <View style={{ gap: 12 }}>
                  {['Everything in Free', 'Unlimited default model messages', 'Priority access to new models', 'API access included'].map((item) => (
                    <CheckLine key={item}>{item}</CheckLine>
                  ))}
                </View>
              </Card>
            </SemanticBlock>
          </View>
          <PremiumNote />
        </PageSection>

        <ClosingBand
          kicker="Ready to get started?"
          lines={[`Try Gab AI for ${String(data.name || 'you').toLowerCase()}`]}
          lede="Join thousands who've already switched to the all-in-one AI platform. Every model, every capability, complete privacy."
          primary={{ label: data.cta, href: '/signup' }}
          secondary={{ label: 'View pricing', href: '/pricing' }}
          trust={[
            `${FREE_USER_STARTING_CREDITS_DISPLAY} free credits to start`,
            'No credit card required',
            'Cancel anytime',
          ]}
        />

        {relatedUseCases.length > 0 ? (
          <PageSection label="Explore Gab AI by profession">
            <SectionIntro kicker="More use cases" lines={['Explore Gab AI by profession']} />
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                marginTop: 20,
              }}
            >
              {relatedUseCases.map((item) => (
                <RelatedRow key={item.slug} item={item} />
              ))}
            </View>
            <View style={{ height: 1, backgroundColor: c.rule }} />
          </PageSection>
        ) : null}

        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
